'use client'

import { useMemo, useState } from 'react'
import { birdiePastRoundsCopy } from 'src/components/BirdieRoundCard/birdieCopy'
import type { Marble } from 'src/types/domain/Marble'
import type { Race } from 'src/types/domain/Race'
import './birdie-board.scss'
import { BirdieRoundCard } from './BirdieRoundCard'
import { BirdieRoundSheet } from './BirdieRoundSheet'
import { BirdieBallDefs } from './GolfBall'
import { birdiePastRounds, type BirdiePastRound } from './pastRounds'
import { buildRoundCardViews } from './roundCardModel'
import { useBirdieRevealClock } from './useBirdieRevealClock'
import { useBirdieTranslate } from './useBirdieTranslate'

type Props = Readonly<{
  /** The page the sidebar already resolved — its own settled, merged, deep-read
   *  window, sliced by `HISTORY.ROUNDS_VISIBLE` / `ROUNDS_STEP`. */
  races: readonly Race[]
  marbles: readonly Marble[]
}>

/**
 * The history sidebar's ROWS, for a Birdie game (B19b item 2).
 *
 * B19 built a whole parallel panel for this. The owner's rule is the opposite —
 * "use what we already have in the sidebar but just make a specific version for
 * birdie time so if this golf it will show that else fallback to the current" —
 * so `<PastRacesSidebar>` keeps its chassis, header, close, paging, loading and
 * empty states, its own deep read and its own "Show more", and ONLY the row
 * changes: the compact `<BirdieRoundCard>` here, `<PastRace>` everywhere else.
 *
 * A component rather than inline JSX for the same reason as the results rail:
 * the reveal clock, the translator and the modal state are hooks, and they must
 * run for a Birdie game only — the shared sidebar gains none of them.
 *
 * The rounds are built from the page the sidebar handed over, in its order. A
 * race with no committed odds, or one still inside its reveal hold, yields no
 * card (`pastRounds`' own contract), so a page can render fewer rows than it
 * holds races for a few seconds after a round settles. That is the anti-spoiler
 * rule the whole board is built on, and the alternative — filtering upstream —
 * would mean the shared list paging over a Birdie-shaped window.
 */
export function BirdieRoundRows({ races, marbles }: Props) {
  const { t, translate, formatPercent, formatInteger } = useBirdieTranslate()
  const copy = useMemo(() => birdiePastRoundsCopy(translate), [translate])
  const nowMs = useBirdieRevealClock(races)

  const rounds = useMemo(
    () => birdiePastRounds({ races, marbles, nowMs, limit: races.length }),
    [marbles, nowMs, races]
  )

  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null)
  // Read when the modal OPENS: "2m ago" must not tick while a player is reading
  // the reveal, and a value read in render would make the card impure.
  const [openedAtMs, setOpenedAtMs] = useState<number>(() => Date.now())

  const views = useMemo(
    () =>
      buildRoundCardViews({
        rounds,
        t: translate,
        formatPercent,
        formatInteger,
        nowMs: openedAtMs
      }),
    [formatInteger, formatPercent, openedAtMs, rounds, translate]
  )

  const selected: BirdiePastRound | undefined = rounds.find(
    (round) => round.roundId === selectedRoundId
  )
  const selectedView = selectedRoundId === null ? undefined : views.get(selectedRoundId)

  return (
    <>
      {/* The sidebar is outside `.birdie-overlay`, so the balls' gradients are
          declared again here or every glyph in a row draws unpainted. Fixed ids,
          identical definitions, therefore idempotent (`GolfBall.tsx`). */}
      <BirdieBallDefs />
      {rounds.map((round) => {
        const view = views.get(round.roundId)
        if (view === undefined) return null
        return (
          <BirdieRoundCard
            density="compact"
            key={round.roundId}
            onOpen={() => {
              setOpenedAtMs(Date.now())
              setSelectedRoundId(round.roundId)
            }}
            view={view}
          />
        )
      })}
      {selected === undefined || selectedView === undefined ? null : (
        <BirdieRoundSheet
          copy={copy}
          onClose={() => {
            setSelectedRoundId(null)
          }}
          round={selected}
          t={t}
          view={selectedView}
        />
      )}
    </>
  )
}
